import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';
import routes from 'sitewide-js/core_pre_webpack/routes';

import { Button } from 'js/design_system/Button';
import { Icon } from 'js/design_system/Icon';
import { Body } from 'js/design_system/Body';
import {
  getProfileBulkDownloadFileList,
  getProfileBulkDownloadPreview,
  getBulkDownloadServerSideZipUrl,
  ProfilePreviewPaper,
  ProfilePreviewResponse,
} from 'js/bulk_download/bulk_download_helper';
import { BundleWork } from 'js/bulk_download/work_cards_shared/types';
import {
  PaperPreviewList,
  GradientOverlay,
} from 'js/bulk_download/work_cards_shared/PaperPreviewList';
import _OuterCard from "ProfilePdfPackCard-OuterCard.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/ProfilePdfPackCard.tsx?styleId=OuterCard";
import _HeaderSection from "ProfilePdfPackCard-HeaderSection.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/ProfilePdfPackCard.tsx?styleId=HeaderSection";
import _StatsLine from "ProfilePdfPackCard-StatsLine.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/ProfilePdfPackCard.tsx?styleId=StatsLine";
import _MetaSep from "ProfilePdfPackCard-MetaSep.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/ProfilePdfPackCard.tsx?styleId=MetaSep";
import _Divider from "ProfilePdfPackCard-Divider.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/ProfilePdfPackCard.tsx?styleId=Divider";
import _InnerCard from "ProfilePdfPackCard-InnerCard.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/ProfilePdfPackCard.tsx?styleId=InnerCard";
import _BottomSection from "ProfilePdfPackCard-BottomSection.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/ProfilePdfPackCard.tsx?styleId=BottomSection";
import _PackIncludesContainer from "ProfilePdfPackCard-PackIncludesContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/ProfilePdfPackCard.tsx?styleId=PackIncludesContainer";
import _CheckList from "ProfilePdfPackCard-CheckList.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/ProfilePdfPackCard.tsx?styleId=CheckList";
import _CheckItem from "ProfilePdfPackCard-CheckItem.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/ProfilePdfPackCard.tsx?styleId=CheckItem";
import _CtaContainer from "ProfilePdfPackCard-CtaContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/ProfilePdfPackCard.tsx?styleId=CtaContainer";
import _FreeCtaButton from "ProfilePdfPackCard-FreeCtaButton.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/ProfilePdfPackCard.tsx?styleId=FreeCtaButton";
import _CtaSubline from "ProfilePdfPackCard-CtaSubline.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/ProfilePdfPackCard.tsx?styleId=CtaSubline";
import _ErrorMessage from "ProfilePdfPackCard-ErrorMessage.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/ProfilePdfPackCard.tsx?styleId=ErrorMessage";


function toBundleWork(paper: ProfilePreviewPaper): BundleWork {
  return {
    id: paper.id,
    title: paper.title,
    authors: paper.authors,
    thumbnailUrl: paper.thumbnailUrl ?? '',
    viewCount: paper.viewCount,
    bucket: paper.bucket,
    year: paper.year ?? undefined,
    relevancy: paper.relevancy ?? undefined,
  };
}

function formatViewCount(views: number): string {
  if (views >= 1_000_000) return `${(views / 1_000_000).toFixed(1)}M`;
  if (views >= 1_000) return `${(views / 1_000).toFixed(0)}k`;
  return String(views);
}

function PackIncludes({
  sameAuthorCount,
  relatedPapersCount,
}: {
  sameAuthorCount: number;
  relatedPapersCount: number;
}) {
  return (
    <PackIncludesContainer>
      <Body size="xs" bold as="p" className="pack-label">
        Included in your PDF pack:
      </Body>
      <CheckList>
        <CheckItem>
          <Icon name="check" size={20} />
          <Body size="xs" as="span">
            {sameAuthorCount} {sameAuthorCount === 1 ? 'paper' : 'papers'} by
            this author
          </Body>
        </CheckItem>
        {relatedPapersCount > 0 && (
          <CheckItem>
            <Icon name="check" size={20} />
            <Body size="xs" as="span">
              {relatedPapersCount}{' '}
              {relatedPapersCount === 1 ? 'paper' : 'papers'} citing this
              author&apos;s work
            </Body>
          </CheckItem>
        )}
        <CheckItem>
          <Icon name="check" size={20} />
          <Body size="xs" as="span">
            Related research in this field
          </Body>
        </CheckItem>
      </CheckList>
    </PackIncludesContainer>
  );
}

interface Props {
  viewedUserId: number;
  viewedUserDisplayName: string;
  worksCount?: number;
  isPremium?: boolean;
}

export const ProfilePdfPackCard: React.FC<Props> = ({
  viewedUserId,
  viewedUserDisplayName,
  worksCount = 0,
  isPremium = false,
}) => {
  const [preview, setPreview] = useState<ProfilePreviewResponse | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (worksCount === 0) return;
    getProfileBulkDownloadPreview(viewedUserId)
      .then(setPreview)
      .catch(() => {});
  }, [viewedUserId, worksCount]);

  const handlePremiumDownload = async () => {
    setIsDownloading(true);
    setError(null);

    try {
      const data = await getProfileBulkDownloadFileList(viewedUserId);
      const validFiles = (data.files || []).filter((f) => f.url && f.filename);

      if (validFiles.length === 0) {
        setError('No downloadable papers found for this author.');
        return;
      }

      const zipUrl = await getBulkDownloadServerSideZipUrl(
        validFiles,
        `${viewedUserDisplayName}_papers`,
        data.zipperUrl,
      );

      if (!zipUrl) {
        setError('Failed to create download package. Please try again.');
        return;
      }

      const link = document.createElement('a');
      link.href = zipUrl;
      link.download = `${viewedUserDisplayName}_papers.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFreeUpgrade = () => {
    window.location.href = routes.checkout_url({
      feature: 'BULK_DOWNLOAD',
      trigger: 'profile-pdf-pack',
    });
  };

  if (worksCount === 0) return null;

  const title = isPremium
    ? `Download a curated PDF Pack of ${viewedUserDisplayName}\u2019s research`
    : `Download ${viewedUserDisplayName}\u2019s most-viewed papers and top related research`;

  const sameAuthorCount = preview?.sameAuthorCount ?? 0;
  const relatedPapersCount = preview?.relatedPapersCount ?? 0;
  const totalViews = preview?.totalViews ?? 0;
  const papers = preview?.papers ?? [];
  const bundleWorks = papers.map(toBundleWork);

  const statParts: string[] = [];
  if (sameAuthorCount > 0) {
    statParts.push(
      `${sameAuthorCount} ${sameAuthorCount === 1 ? 'paper' : 'papers'}`,
    );
  }
  if (totalViews > 0) {
    statParts.push(`${formatViewCount(totalViews)} views`);
  }

  return (
    <OuterCard>
      <HeaderSection>
        <Body size="lg" bold as="h3">
          {title}
        </Body>
        {statParts.length > 0 && (
          <StatsLine>
            {statParts.map((part, i) => (
              <React.Fragment key={i}>
                {i > 0 && <MetaSep>|</MetaSep>}
                <Body size="xs" as="span">
                  {part}
                </Body>
              </React.Fragment>
            ))}
          </StatsLine>
        )}
      </HeaderSection>

      <Divider />

      <InnerCard>
        <PaperPreviewList
          works={bundleWorks}
          maxHeight="280px"
          footer={!isPremium ? <GradientOverlay /> : undefined}
        />

        <BottomSection>
          <PackIncludes
            sameAuthorCount={sameAuthorCount}
            relatedPapersCount={relatedPapersCount}
          />

          {error && (
            <ErrorMessage>
              <Body size="sm" as="p">
                {error}
              </Body>
            </ErrorMessage>
          )}

          <CtaContainer>
            {isPremium ? (
              <Button
                icon="download"
                fullWidth
                onClick={handlePremiumDownload}
                disabled={isDownloading}
              >
                {isDownloading ? 'Preparing Download...' : 'Download PDF Pack'}
              </Button>
            ) : (
              <FreeCtaButton onClick={handleFreeUpgrade}>
                <Body size="md" bold>
                  Try Premium for $1
                </Body>
                <CtaSubline>
                  <Body size="sm" as="span">
                    And Download your PDF Pack
                  </Body>
                  <Icon name="arrow_forward" size={16} />
                </CtaSubline>
              </FreeCtaButton>
            )}
          </CtaContainer>
        </BottomSection>
      </InnerCard>
    </OuterCard>
  );
};

const OuterCard = /*#__PURE__*/styled("div", null, {
  displayName: "OuterCard",
  styles: _OuterCard
});

const HeaderSection = /*#__PURE__*/styled("div", null, {
  displayName: "HeaderSection",
  styles: _HeaderSection
});

const StatsLine = /*#__PURE__*/styled("div", null, {
  displayName: "StatsLine",
  styles: _StatsLine
});

const MetaSep = /*#__PURE__*/styled(Body, null, {
  displayName: "MetaSep",
  styles: _MetaSep,
  attrs: props => ({
    ...props,
    size: 'xs',
    as: 'span'
  })
});

const Divider = /*#__PURE__*/styled("div", null, {
  displayName: "Divider",
  styles: _Divider
});

const InnerCard = /*#__PURE__*/styled("div", null, {
  displayName: "InnerCard",
  styles: _InnerCard
});

const BottomSection = /*#__PURE__*/styled("div", null, {
  displayName: "BottomSection",
  styles: _BottomSection
});

const PackIncludesContainer = /*#__PURE__*/styled("div", null, {
  displayName: "PackIncludesContainer",
  styles: _PackIncludesContainer
});

const CheckList = /*#__PURE__*/styled("div", null, {
  displayName: "CheckList",
  styles: _CheckList
});

const CheckItem = /*#__PURE__*/styled("div", null, {
  displayName: "CheckItem",
  styles: _CheckItem
});

const CtaContainer = /*#__PURE__*/styled("div", null, {
  displayName: "CtaContainer",
  styles: _CtaContainer
});

const FreeCtaButton = /*#__PURE__*/styled(Button, null, {
  displayName: "FreeCtaButton",
  styles: _FreeCtaButton
});

const CtaSubline = /*#__PURE__*/styled("div", null, {
  displayName: "CtaSubline",
  styles: _CtaSubline
});

const ErrorMessage = /*#__PURE__*/styled("div", null, {
  displayName: "ErrorMessage",
  styles: _ErrorMessage
});
